import { useEffect, useState } from "react";

import { PageLayout } from "../../components/PageLayout";
import { Badge, EmptyState, InputField, Text, View } from "../../components/base";
import { IconButton } from "../../components/base/IconButton";
import { AdminColumn, AdminDetailDrawer, AdminTable, Field, Section, useAdminFetch } from "../../components/admin";
import { LeadRow } from "../../types/admin";
import { getColorForName } from "../../utils/colors";
import { formatDateShort, formatRelative } from "../../utils/adminFormat";

const PAGE_SIZE = 50;

type LeadListResponse = {
  data: LeadRow[];
  metadata: { page: number; limit: number; total: number };
};

const fullName = (lead: LeadRow) => `${lead.firstName} ${lead.lastName}`.trim();

const initials = (lead: LeadRow) => `${lead.firstName.charAt(0)}${lead.lastName.charAt(0)}`.toUpperCase();

const columns: AdminColumn<LeadRow>[] = [
  {
    key: "name",
    header: "Name",
    width: "1.4fr",
    render: (lead) => (
      <View className="flex flex-row items-center gap-2.5 min-w-0">
        <View className={`w-8 h-8 rounded-full shrink-0 flex items-center justify-center ${getColorForName(fullName(lead) || lead.id)}`}>
          <Text className="text-xs font-medium text-white">{initials(lead)}</Text>
        </View>
        <Text className="truncate font-medium text-gray-900 dark:text-gray-100">{fullName(lead) || "—"}</Text>
      </View>
    ),
  },
  {
    key: "email",
    header: "Email",
    width: "1.4fr",
    render: (lead) => <Text className="block truncate text-gray-600 dark:text-gray-300">{lead.email}</Text>,
  },
  {
    key: "company",
    header: "Company",
    render: (lead) => (lead.company ? <Text className="block truncate">{lead.company}</Text> : <Text className="text-gray-400 dark:text-gray-500">—</Text>),
  },
  {
    key: "category",
    header: "Category",
    render: (lead) => (lead.category ? <Badge variant="default">{lead.category}</Badge> : null),
  },
  {
    key: "createdAt",
    header: "Received",
    width: "120px",
    align: "right",
    sortValue: (lead) => lead.createdAt,
    render: (lead) => <Text className="text-gray-500 dark:text-gray-400">{formatDateShort(lead.createdAt)}</Text>,
  },
];

export const AdminLeadsPage = () => {
  const [page, setPage] = useState(1);
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [selectedLead, setSelectedLead] = useState<LeadRow | null>(null);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedQuery(query);
      setPage(1);
    }, 300);
    return () => clearTimeout(timeout);
  }, [query]);

  const url = `/v1/leads?page=${page}&limit=${PAGE_SIZE}${debouncedQuery ? `&q=${encodeURIComponent(debouncedQuery)}` : ""}`;
  const { data, isLoading, error, refresh } = useAdminFetch<LeadListResponse>(url);

  const leads = data?.data ?? [];
  const total = data?.metadata.total ?? 0;

  return (
    <PageLayout
      title="Leads"
      description="Inbound lead form submissions from the marketing site."
      headerAction={
        <IconButton
          icon="refresh"
          variant="info"
          onClick={refresh}
          title="Refresh"
        />
      }
    >
      <View className="mt-6 flex flex-col gap-4">
        <View className="max-w-sm">
          <InputField
            value={query}
            onChange={setQuery}
            placeholder="Search by name, email, or company…"
          />
        </View>

        {error ? (
          <EmptyState
            icon="warning-amber"
            title="Failed to load leads"
            description={error}
          />
        ) : (
          <AdminTable
            columns={columns}
            rows={leads}
            rowKey={(lead) => lead.id}
            onRowClick={setSelectedLead}
            selectedKey={selectedLead?.id ?? null}
            isLoading={isLoading}
            emptyState={{
              icon: "target",
              title: debouncedQuery ? "No leads match your search" : "No leads yet",
              description: debouncedQuery ? "Try a different name, email, or company." : "Lead form submissions will show up here.",
            }}
            defaultSort={{ key: "createdAt", dir: "desc" }}
            pagination={{ page, pageSize: PAGE_SIZE, total, onPageChange: setPage }}
          />
        )}
      </View>

      <AdminDetailDrawer
        isOpen={selectedLead !== null}
        onClose={() => setSelectedLead(null)}
        title={selectedLead ? fullName(selectedLead) || selectedLead.email : ""}
        subtitle={selectedLead ? formatRelative(selectedLead.createdAt) : undefined}
      >
        {selectedLead && (
          <>
            <Section title="Contact">
              <View className="flex flex-col gap-2">
                <Field label="Email">
                  <a
                    href={`mailto:${selectedLead.email}`}
                    className="text-gray-600 dark:text-gray-300 hover:text-brand dark:hover:text-brand-light transition-colors break-all"
                  >
                    {selectedLead.email}
                  </a>
                </Field>
                <Field label="Phone">
                  {selectedLead.phone ? (
                    <a
                      href={`tel:${selectedLead.phone}`}
                      className="text-gray-600 dark:text-gray-300 hover:text-brand dark:hover:text-brand-light transition-colors"
                    >
                      {selectedLead.phone}
                    </a>
                  ) : (
                    <Text className="text-gray-400 dark:text-gray-500">—</Text>
                  )}
                </Field>
                <Field label="Company">
                  {selectedLead.company ? <Text>{selectedLead.company}</Text> : <Text className="text-gray-400 dark:text-gray-500">—</Text>}
                </Field>
                <Field label="Category">
                  {selectedLead.category ? (
                    <Badge variant="default">{selectedLead.category}</Badge>
                  ) : (
                    <Text className="text-gray-400 dark:text-gray-500">—</Text>
                  )}
                </Field>
              </View>
            </Section>

            <Section title="Message">
              {selectedLead.message ? (
                <Text className="block whitespace-pre-wrap text-sm text-gray-900 dark:text-gray-100">{selectedLead.message}</Text>
              ) : (
                <Text className="text-sm text-gray-400 dark:text-gray-500">No message</Text>
              )}
            </Section>

            <Section title="Meta">
              <View className="flex flex-col gap-2">
                <Field
                  label="ID"
                  mono
                >
                  {selectedLead.id}
                </Field>
                <Field label="Received">{formatDateShort(selectedLead.createdAt)}</Field>
              </View>
            </Section>
          </>
        )}
      </AdminDetailDrawer>
    </PageLayout>
  );
};
